import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { RetentionCoupon } from '../../types/account.types';
import { AccountService } from '../../account.service';
import { NotificationService } from '../../notification/notification.service';
import { CommonModule } from '@angular/common';
import { OrderService } from '../../order.service';
import { SubscriptionsService } from '../../../settings/account/subscriptions/subscriptions.service';

@Component({
  selector: 'app-unable-to-cancel-subscription',
  imports: [CommonModule],
  templateUrl: './unable-to-cancel-subscription.component.html',
  styleUrl: './unable-to-cancel-subscription.component.scss'
})
export class UnableToCancelSubscriptionComponent {

  #activeModal = inject(NgbActiveModal);
  #accountService = inject(AccountService);
  #notificationService = inject(NotificationService);
  #orderService = inject(OrderService);
  #subscriptionsService = inject(SubscriptionsService);

  retentionCoupons = input<{
    coupons: RetentionCoupon[],
    minimumSubscriptionRequired: boolean
    subscriptionId: number
  } | undefined>();

  currentCouponIndex = signal(0);
  isApplyingCoupon = signal(false);
  showFinalMessage = signal(false);

  supportEmail = "info@farmboxdl.com"

  // Sort coupons by tier and get current coupon
  sortedCoupons = computed(() => {
    const coupons = this.retentionCoupons()?.coupons ?? [];
    return [...coupons].sort((a, b) => a.tier - b.tier);
  });

  currentCoupon = computed(() => {
    const coupons = this.sortedCoupons();
    const index = this.currentCouponIndex();
    return coupons[index] || null;
  });

  hasMoreCoupons = computed(() => {
    return this.currentCouponIndex() < this.sortedCoupons().length - 1;
  });

  showCouponOffer = computed(() => {
    return this.currentCoupon() !== null && !this.showFinalMessage();
  });

  constructor() {
    // Initialize: if no coupons, show final message immediately
    effect(() => {
      const coupons = this.sortedCoupons();
      if (coupons.length === 0 && this.retentionCoupons()?.minimumSubscriptionRequired) {
        this.showFinalMessage.set(true);
      }
    });
  }

  onRejectCoupon() {
    if (this.hasMoreCoupons()) {
      // Show next coupon
      this.currentCouponIndex.update(index => index + 1);
    } else {
      // No more coupons, show final message
      if (this.retentionCoupons()?.minimumSubscriptionRequired) {
        this.showFinalMessage.set(true);
      } else {
        this.#subscriptionsService.cancel(this.retentionCoupons()?.subscriptionId ?? 0, 1)
        this.#activeModal.close({ accepted: false });
      }
    }
  }

  onAcceptCoupon() {
    const coupon = this.currentCoupon();
    if (!coupon) return;

    this.isApplyingCoupon.set(true);

    this.#accountService.applyRetentionCoupon(coupon.id).subscribe({
      next: (response) => {
        this.#notificationService.show({
          text: `${coupon.name} has been successfully applied to your account!`,
          type: 'success'
        });
        this.#orderService.getOrder();
        this.#activeModal.close({ accepted: true, couponId: coupon.id });
      },
      error: (error) => {
        this.#notificationService.show({
          text: 'There was an error applying the coupon. Please try again.',
          type: 'error'
        });
        this.isApplyingCoupon.set(false);
      }
    });
  }

  closeModal() {
    this.#activeModal.dismiss();
  }
}
